import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  RANGE_PRESETS,
  lastNPeriods,
  resolveRange,
  resolveAppliedCustomRange,
  reportsRangeFromQuery,
  toApiDates,
} from './pnlDateRanges';
import {
  coverageLabel,
  fleetMilesCoverageNotice,
  unitMilesCoverage,
} from './llamaChatDisplay';
import './FleetPnlReports.css';

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function provPill(provenance) {
  if (!provenance || provenance === 'actual') return null;
  const map = {
    estimated: { cls: 'pill warning', label: 'est' },
    stale: { cls: 'pill warning', label: 'stale' },
    missing: { cls: 'pill neutral', label: 'missing' },
  };
  const m = map[provenance] || { cls: 'pill neutral', label: provenance };
  return (
    <span className={m.cls}>
      <span className="d" />
      {m.label}
    </span>
  );
}

/** Honest money cell — absent → —, never $0 for missing */
function ProvMoney({ line, emphasize }) {
  if (!line || line.value == null) {
    return (
      <span className="fd-pnl-cell mono">
        —
        {provPill(line?.provenance || 'missing')}
      </span>
    );
  }
  const cls = [
    'fd-pnl-cell',
    'mono',
    emphasize ? 'fd-pnl-net' : '',
  ].filter(Boolean).join(' ');

  const tipParts = [];
  if (line.partial) tipParts.push('PARTIAL — not final margin');
  if (Array.isArray(line.missingInputs) && line.missingInputs.length) {
    tipParts.push(`Excluded/missing: ${line.missingInputs.join(', ')}`);
  }
  if (line.basis) tipParts.push(`Basis: ${line.basis}`);
  if (line.note) tipParts.push(line.note);

  return (
    <span className={cls} title={tipParts.join(' · ') || undefined}>
      {money(line.value)}
      {provPill(line.provenance)}
      {line.partial && (
        <span className="pill rust" title={tipParts.join(' · ')}>
          <span className="d" />
          partial
        </span>
      )}
    </span>
  );
}

function sortUnits(units, key, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  return [...units].sort((a, b) => {
    const av = key === 'unitNumber'
      ? String(a.unitNumber || '')
      : key === 'loads'
        ? (a.revenue?.loadCount ?? -1)
        : key === 'perLoadAvg'
          ? (a.revenue?.perLoadAvg ?? -Infinity)
          : (a[key]?.value ?? -Infinity);
    const bv = key === 'unitNumber'
      ? String(b.unitNumber || '')
      : key === 'loads'
        ? (b.revenue?.loadCount ?? -1)
        : key === 'perLoadAvg'
          ? (b.revenue?.perLoadAvg ?? -Infinity)
          : (b[key]?.value ?? -Infinity);
    if (typeof av === 'string') return mul * av.localeCompare(bv);
    if (av === bv) return String(a.unitNumber).localeCompare(String(b.unitNumber));
    return mul * (av - bv);
  });
}

function coverageLine(attribution) {
  if (!attribution) return null;
  const total = attribution.deliveryInWindow ?? 0;
  const company = attribution.attributedToCompanyUnits ?? 0;
  const oo = attribution.attributedToOwnerOpExcluded ?? 0;
  const unassigned = attribution.unassignedCount ?? 0;
  const unresolved = attribution.unresolvedCount ?? 0;
  return (
    <div className="fd-pnl-coverage">
      <span className="eyebrow">Attribution coverage</span>
      {' '}
      <span className="mono">
        {company} of {total} delivery loads attributed to company
      </span>
      {' · '}
      <span className="mono">{oo} owner-operator excluded</span>
      {' · '}
      <span className="mono">{unassigned} unassigned</span>
      {unresolved > 0 && (
        <>
          {' · '}
          <span className="mono">{unresolved} unresolved</span>
        </>
      )}
      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-500)' }}>
        Margin before driver = revenue − fuel − fixed − factoring.
      </div>
    </div>
  );
}

function milesCoverageLine(perUnit) {
  const notice = fleetMilesCoverageNotice(perUnit);
  const units = (perUnit || []).filter((unit) => unitMilesCoverage(unit));
  if (!notice && !units.length) return null;
  return (
    <div className="fd-pnl-coverage">
      <span className="eyebrow">Mileage coverage</span>
      {' '}
      <span className="mono">
        {notice || 'Recorded driving in this window as of the last fetch.'}
      </span>
      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-500)' }}>
        Same labels as Chat. Incomplete days keep fuel from looking finished.
      </div>
    </div>
  );
}

function exportCsvWithProvenance(rows, rangeLabel) {
  const header = [
    'unit', 'companyPnl', 'eld', 'loads',
    'revenue', 'revenue_provenance',
    'per_load_avg',
    'fuel', 'fuel_provenance',
    'factoring', 'factoring_provenance',
    'fixed', 'fixed_provenance',
    'margin_before_driver', 'margin_before_driver_provenance',
    'verification_status',
    'fuel_incomplete',
    'no_motive_miles_days',
    'date_not_confirmed_loads',
    'seat_note',
  ];
  const body = rows.map((u) => [
    u.unitNumber,
    u.companyPnl ? 'Y' : 'N',
    u.eldConnected ? 'Y' : 'N',
    u.revenue?.loadCount ?? '',
    u.revenue?.value ?? '',
    u.revenue?.provenance ?? 'missing',
    u.revenue?.perLoadAvg ?? '',
    u.fuelEstimated?.value ?? '',
    u.fuelEstimated?.provenance ?? 'missing',
    u.factoring?.value ?? '',
    u.factoring?.provenance ?? 'missing',
    u.unitFixed?.value ?? '',
    u.unitFixed?.provenance ?? 'missing',
    u.marginBeforeDriver?.value ?? '',
    u.marginBeforeDriver?.provenance ?? 'missing',
    u.verificationStatus || '',
    u.fuelIncomplete ? 'Y' : 'N',
    (u.fuelIncompleteDays || []).map((day) => (
      `${day.date}:${(day.events || []).map((event) => event.loadNumber).join('|')}`
    )).join(';'),
    (u.dateNotConfirmedLoads || []).map((load) => load.loadNumber).join('|'),
    u.seat?.partialSeat ? (u.seat.note || 'partial seat') : '',
  ].map((c) => {
    const s = String(c);
    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(','));

  const blob = new Blob([[header.join(','), ...body].join('\n') + '\n'], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fleet-pnl-${rangeLabel.replace(/\s+/g, '-').toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Reports tab — windows fleet-pnl-margin-before-driver-v1. Does not recompute P&L.
 */
const FleetPnlReports = ({ headers, initialQuery = {} }) => {
  const fromUrl = reportsRangeFromQuery(initialQuery);
  // Default Last Month so July → June (gate-verified month) unless the chat link named a window.
  const [preset, setPreset] = useState(fromUrl.preset);
  const [customStart, setCustomStart] = useState(fromUrl.customStart);
  const [customEnd, setCustomEnd] = useState(fromUrl.customEnd);
  const [range, setRange] = useState(fromUrl.range);
  const [pnl, setPnl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('marginBeforeDriver');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [trendGrain, setTrendGrain] = useState('month');
  const [trendRows, setTrendRows] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const applyPreset = useCallback((id) => {
    setPreset(id);
    if (id !== 'custom') {
      setRange(resolveRange(id));
      setSelectedUnit(null);
    }
  }, []);

  const applyCustom = useCallback(() => {
    const next = resolveAppliedCustomRange(customStart, customEnd, range);
    setCustomStart(next.startDate);
    setCustomEnd(next.endDate);
    setPreset('custom');
    setRange(next);
    setSelectedUnit(null);
  }, [customStart, customEnd, range]);

  const fetchPnl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = toApiDates(range.startDate, range.endDate);
      const res = await axios.get('/api/fleet-intelligence/unit-pnl', {
        headers,
        params: { startDate, endDate, _t: Date.now() },
      });
      if (!res.data?.success && res.data?.engine == null && !res.data?.perUnit) {
        throw new Error(res.data?.message || 'unit-pnl failed');
      }
      setPnl(res.data);
    } catch (err) {
      setPnl(null);
      setError(err?.response?.data?.message || err.message || 'Failed to load P&L');
    }
    setLoading(false);
  }, [headers, range.startDate, range.endDate]);

  useEffect(() => {
    fetchPnl();
  }, [fetchPnl]);

  const companyUnits = useMemo(() => {
    const list = (pnl?.perUnit || []).filter((u) => u.companyPnl === true && !u.archived);
    return sortUnits(list, sortKey, sortDir);
  }, [pnl, sortKey, sortDir]);

  const ooUnits = useMemo(
    () => (pnl?.perUnit || []).filter((u) => u.excludedOwnerOp === true && !u.archived),
    [pnl]
  );

  const totals = pnl?.fleetTotals || {};

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setSortDir(key === 'unitNumber' ? 'asc' : 'desc');
    }
  };

  const loadTrend = useCallback(async (unitNumber) => {
    setTrendLoading(true);
    setTrendRows([]);
    try {
      const periods = lastNPeriods(range.endDate, 6, trendGrain);
      const results = await Promise.all(
        periods.map(async (p) => {
          const { startDate, endDate } = toApiDates(p.startDate, p.endDate);
          const res = await axios.get('/api/fleet-intelligence/unit-pnl', {
            headers,
            params: { startDate, endDate, _t: Date.now() },
          });
          const unit = (res.data?.perUnit || []).find((u) => String(u.unitNumber) === String(unitNumber));
          return {
            label: p.label,
            startDate: p.startDate,
            endDate: p.endDate,
            loads: unit?.revenue?.loadCount ?? 0,
            revenue: unit?.revenue || { value: null, provenance: 'missing' },
            marginBeforeDriver: unit?.marginBeforeDriver || { value: null, provenance: 'missing' },
          };
        })
      );
      setTrendRows(results);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Trend load failed');
    }
    setTrendLoading(false);
  }, [headers, range.endDate, trendGrain]);

  useEffect(() => {
    if (selectedUnit) loadTrend(selectedUnit);
  }, [selectedUnit, trendGrain, loadTrend]);

  const maxAbsMargin = useMemo(() => {
    const vals = trendRows.map((r) => Math.abs(r.marginBeforeDriver?.value || 0));
    return Math.max(1, ...vals);
  }, [trendRows]);

  if (selectedUnit) {
    return (
      <div className="fd-pnl-reports">
        <div className="fd-pnl-toolbar">
          <button type="button" className="fd-pnl-back" onClick={() => setSelectedUnit(null)}>
            ← Ranking
          </button>
          <div>
            <div className="eyebrow">Single-unit trend</div>
            <h2 className="page-title" style={{ margin: 0 }}>Unit {selectedUnit}</h2>
            <div style={{ fontSize: 12, color: 'var(--ink-500)' }}>
              Last 6 {trendGrain === 'week' ? 'weeks' : 'months'} ending {range.endDate} · engine {pnl?.engine || 'fleet-pnl-margin-before-driver-v1'}
            </div>
          </div>
        </div>

        <div className="fd-pnl-grain">
          {['month', 'week'].map((g) => (
            <button
              key={g}
              type="button"
              className={`fd-pnl-preset ${trendGrain === g ? 'is-active' : ''}`}
              onClick={() => setTrendGrain(g)}
            >
              {g === 'month' ? 'Months' : 'Weeks'}
            </button>
          ))}
        </div>

        {trendLoading && <div className="fd-pnl-loading">Loading trend…</div>}
        {!trendLoading && (
          <>
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Loads</th>
                    <th>Revenue</th>
                    <th>Margin before driver</th>
                  </tr>
                </thead>
                <tbody>
                  {trendRows.map((r) => (
                    <tr key={`${r.startDate}_${r.endDate}`}>
                      <td className="mono">{r.label}</td>
                      <td className="mono">{r.loads}</td>
                      <td><ProvMoney line={r.revenue} /></td>
                      <td><ProvMoney line={r.marginBeforeDriver} emphasize /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card" style={{ padding: 16 }}>
              <div className="eyebrow">Margin before driver over time</div>
              <div className="fd-pnl-trend-bars">
                {trendRows.map((r) => {
                  const v = r.marginBeforeDriver?.value;
                  const pct = v == null ? 0 : (Math.abs(v) / maxAbsMargin) * 100;
                  return (
                    <div className="fd-pnl-trend-row" key={`bar_${r.startDate}`}>
                      <span className="mono" style={{ fontSize: 12 }}>{r.label}</span>
                      <div className="fd-pnl-bar-track">
                        <div className="fd-pnl-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <ProvMoney line={r.marginBeforeDriver} emphasize />
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="fd-pnl-reports">
      <div className="fd-pnl-toolbar">
        <div>
          <div className="eyebrow">Fleet P&amp;L Reports</div>
          <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>
            Windows <span className="mono">{pnl?.engine || 'fleet-pnl-margin-before-driver-v1'}</span>
            {' · '}
            {range.label} ({range.startDate} → {range.endDate})
          </div>
        </div>
        <button
          type="button"
          className="fd-pnl-export"
          disabled={!companyUnits.length}
          onClick={() => exportCsvWithProvenance(
            [...companyUnits, ...ooUnits],
            range.label
          )}
          title="CSV includes provenance columns"
        >
          Export CSV
        </button>
      </div>

      <div className="fd-pnl-toolbar">
        <div className="fd-pnl-presets">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fd-pnl-preset ${preset === p.id ? 'is-active' : ''}`}
              onClick={() => applyPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="fd-pnl-custom">
            <input type="date" value={customStart || range.startDate} onChange={(e) => setCustomStart(e.target.value)} />
            <span className="mono">→</span>
            <input type="date" value={customEnd || range.endDate} onChange={(e) => setCustomEnd(e.target.value)} />
            <button type="button" className="primary" onClick={applyCustom}>Apply</button>
          </div>
        )}
        {preset !== 'custom' && (
          <button type="button" className="fd-pnl-preset" onClick={() => {
            setCustomStart(range.startDate);
            setCustomEnd(range.endDate);
            setPreset('custom');
          }}>
            Custom dates…
          </button>
        )}
      </div>

      {error && <div className="fd-pnl-error">{error}</div>}
      {loading && <div className="fd-pnl-loading">Loading unit P&amp;L…</div>}

      {!loading && pnl && (
        <>
          {pnl.window?.inProgress && (
            <div className="fd-pnl-coverage" role="status">
              <span className="pill warning"><span className="d" />in progress</span>
              {' '}
              <span className="mono">This period is not finished; totals can still change.</span>
            </div>
          )}
          {pnl.dieselHealth && pnl.dieselHealth.tone !== 'ok' && (
            <div className="fd-pnl-error" role="alert" style={{
              background: pnl.dieselHealth.tone === 'danger' ? '#fef2f2' : '#fff7ed',
              color: pnl.dieselHealth.tone === 'danger' ? '#991b1b' : '#9a3412',
              marginBottom: 12
            }}>
              <strong>{pnl.dieselHealth.title}</strong>
              <div style={{ marginTop: 4 }}>{pnl.dieselHealth.detail}</div>
            </div>
          )}
          <div className="fd-pnl-hero">
            <div className="card">
              <p className="eyebrow">Company revenue</p>
              <p className="fd-pnl-hero-num">{money(totals.revenue?.value)}</p>
              <span className="pill neutral"><span className="d" />delivery-date window</span>
            </div>
            <div className="card">
              <p className="eyebrow">Total costs</p>
              <p className="fd-pnl-hero-num">{money(totals.totalCost?.value)}</p>
              <span className="pill neutral"><span className="d" />fuel + fixed + factoring</span>
            </div>
            <div className="card">
              <p className="eyebrow">Margin before driver</p>
              <p className="fd-pnl-hero-num">{money(totals.marginBeforeDriver?.value)}</p>
              <span className="pill warning"><span className="d" />Estimate</span>
            </div>
            <div className="card">
              <p className="eyebrow">Company loads</p>
              <p className="fd-pnl-hero-num">{totals.loadCount ?? 0}</p>
              <span className="pill neutral"><span className="d" />{totals.unitCount ?? companyUnits.length} units</span>
            </div>
            <div className="card">
              <p className="eyebrow">Diesel (week × ops)</p>
              <p className="fd-pnl-hero-num">
                {pnl.diesel?.value != null ? `$${Number(pnl.diesel.value).toFixed(3)}` : '—'}
              </p>
              {pnl.dieselHealth?.tone === 'ok'
                ? <span className="pill success"><span className="d" />EIA API live</span>
                : pnl.dieselHealth?.tone === 'warn'
                  ? <span className="pill rust"><span className="d" />EIA API down</span>
                  : <span className="pill rust"><span className="d" />EIA unavailable</span>}
              {provPill(pnl.diesel?.provenance)}
              {pnl.operations?.states?.[0] && (
                <div className="muted" style={{ marginTop: 6, fontSize: 11 }}>
                  {pnl.operations.states.slice(0, 3).map((s) => s.jurisdiction).join(' · ')}
                  {pnl.dieselWeeks?.length ? ` · ${pnl.dieselWeeks.length} EIA weeks` : ''}
                </div>
              )}
            </div>
          </div>

          {coverageLine(pnl.attribution)}
          {milesCoverageLine(companyUnits)}
          {(pnl.dataQuality?.fuelIncompleteCount || 0) > 0 && (
            <div className="fd-pnl-coverage" role="status">
              <span className="pill rust"><span className="d" />Fuel incomplete</span>
              {' '}
              <span className="mono">
                {pnl.dataQuality.fuelIncompleteCount} pickup/delivery day
                {pnl.dataQuality.fuelIncompleteCount === 1 ? '' : 's'} have no Motive miles and count $0 fuel.
              </span>
            </div>
          )}
          {(pnl.attribution?.dateNotConfirmedCount || 0) > 0 && (
            <div className="fd-pnl-coverage" role="status">
              <span className="pill rust"><span className="d" />date not confirmed</span>
              {' '}
              <span className="mono">
                {pnl.attribution.dateNotConfirmedCount} load
                {pnl.attribution.dateNotConfirmedCount === 1 ? '' : 's'} use operational dropDate because Motive arrival and appointment are unavailable.
              </span>
            </div>
          )}

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  {[
                    ['unitNumber', 'Unit'],
                    ['loads', 'Loads'],
                    ['revenue', 'Revenue'],
                    ['perLoadAvg', '$/load'],
                    ['fuelEstimated', 'Fuel'],
                    ['factoring', 'Factoring'],
                    ['unitFixed', 'Fixed'],
                    ['marginBeforeDriver', 'Margin before driver'],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      className={sortKey === key ? 'is-sorted' : ''}
                      onClick={() => toggleSort(key)}
                    >
                      {label}{sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companyUnits.map((u) => (
                  <tr key={u.unitNumber} onClick={() => setSelectedUnit(u.unitNumber)}>
                    <td>
                      <div className="fd-pnl-unit-cell">
                        <strong>{u.unitNumber}</strong>
                        <div className="fd-pnl-flags">
                          {u.eldConnected
                            ? <span className="pill success"><span className="d" />ELD</span>
                            : <span className="pill neutral"><span className="d" />no-ELD</span>}
                          {u.seat?.partialSeat && (
                            <span className="pill rust" title={u.seat.note}>
                              <span className="d" />
                              partial seat
                            </span>
                          )}
                          {u.unitFixed?.provenance === 'stale' && (
                            <span className="pill warning"><span className="d" />stale costs</span>
                          )}
                          {unitMilesCoverage(u)?.needsSpokenCatchup && (
                            <span className="pill rust" title={coverageLabel(unitMilesCoverage(u).status)}>
                              <span className="d" />
                              {coverageLabel(unitMilesCoverage(u).status) || 'Mileage catching up'}
                            </span>
                          )}
                          {unitMilesCoverage(u)?.status === 'no_recorded_driving' && (
                            <span className="pill neutral" title={coverageLabel('no_recorded_driving')}>
                              <span className="d" />
                              no recorded driving
                            </span>
                          )}
                          {(u.dateNotConfirmedLoads || []).length > 0 && (
                            <span
                              className="pill rust"
                              title={(u.dateNotConfirmedLoads || [])
                                .map((load) => `Load ${load.loadNumber}: date not confirmed`)
                                .join('\n')}
                            >
                              <span className="d" />
                              date not confirmed
                            </span>
                          )}
                          {u.fuelIncomplete && (
                            <span
                              className="pill rust"
                              title={(u.fuelIncompleteDays || []).map((day) => (
                                `${day.date}: ${(day.events || [])
                                  .map((event) => `${event.loadNumber} ${event.event}`)
                                  .join(', ')} — No Motive miles`
                              )).join('\n')}
                            >
                              <span className="d" />
                              Fuel incomplete
                            </span>
                          )}
                          <span
                            className="pill warning"
                            title="Fuel-card and factoring reconciliation imports are not available yet"
                          >
                            <span className="d" />
                            Estimate
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="mono">
                      {u.revenue?.loadCount ?? 0}
                    </td>
                    <td><ProvMoney line={u.revenue} /></td>
                    <td className="mono">
                      {u.revenue?.perLoadAvg != null ? money(u.revenue.perLoadAvg) : '—'}
                    </td>
                    <td><ProvMoney line={u.fuelEstimated} /></td>
                    <td><ProvMoney line={u.factoring} /></td>
                    <td><ProvMoney line={u.unitFixed} /></td>
                    <td><ProvMoney line={u.marginBeforeDriver} emphasize /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fd-pnl-oo card" style={{ padding: '8px 16px' }}>
            <details>
              <summary>
                Owner-operators excluded from company P&amp;L
                {' '}
                ({ooUnits.reduce((s, u) => s + (u.loadAttribution?.driverUnitLoadCount || 0), 0)} loads attributed · no company math)
              </summary>
              <table className="tbl" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Attributed loads</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {ooUnits.map((u) => (
                    <tr key={u.unitNumber} style={{ cursor: 'default' }}>
                      <td className="mono">{u.unitNumber}</td>
                      <td className="mono">{u.loadAttribution?.driverUnitLoadCount ?? 0}</td>
                      <td style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                        {u.exclusionReason || 'Excluded — separate OO model later'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        </>
      )}
    </div>
  );
};

export default FleetPnlReports;
